import React, { FC, useRef, useState } from 'react';
import { Tooltip, TooltipProps, shouldEnableTooltip } from '../Tooltip/Tooltip';
import { Typography, TypographyProps } from '../Typography/Typography';
import { styled, css } from '../../styles';
import { BpDesignSystem } from '../../types';

export type TypographyTooltipProps = BpDesignSystem.PropsWithChildren<TypographyProps> & {
  maxWidth?: number;
  slotProps?: {
    Tooltip?: Omit<TooltipProps, 'children'>;
  };
};

/**
 * Typography component that displays a tooltip when the specified max width is reached.
 * If no max width is specified the parent component must have a width. Child component
 * needs to be text. As is built with the UDS Typography component any Typography
 * component properties can be used.
 *
 */
export const TypographyTooltip: FC<TypographyTooltipProps> = ({
  maxWidth,
  className,
  children,
  slotProps,
  style,
  sx,
  ...props
}) => {
  const [showTooltip, setTooltipState] = useState(false);
  const typographyRef = useRef<HTMLDivElement | null>(null);

  return (
    <StyledTypographyMui
      className={className}
      $maxWidth={maxWidth}
      ref={typographyRef}
      style={style}
      noWrap
      sx={sx}
      onMouseEnter={() => setTooltipState(shouldEnableTooltip(typographyRef.current))}
    >
      <Tooltip title={children} disabled={!showTooltip} placement="top" {...slotProps?.Tooltip}>
        <StyledTypography component="span" {...props}>
          {children}
        </StyledTypography>
      </Tooltip>
    </StyledTypographyMui>
  );
};

const StyledTypographyMui = styled(Typography)<{ $maxWidth?: number }>`
  ${({ $maxWidth }) => ($maxWidth ? css`max-width: ${$maxWidth}px` : '')};
`;

const StyledTypography = styled(Typography)`
  // Allow UDS Typography component to be used and for styles to cascade.
  &,
  div {
    display: inline;
    overflow: inherit;
    white-space: inherit;
    text-overflow: inherit;
  }
`;
